// src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import LoginForm from './components/auth/LoginForm';
import Layout from './layouts/Layout';

// Importar ClientPortalApp
import ClientPortalApp from './ClientPortalApp';

// Importar TechnicianPortalApp
import TechnicianPortalApp from './TechnicianPortalApp';

// Importar el componente PrivateRoute correcto
import PrivateRoute from './components/auth/PrivateRoute';

// Importar componentes del panel de administración
import Dashboard from './pages/Dashboard';
import ClientList from './pages/clients/ClientList';
import AddEditClient from './pages/clients/AddEditClient';
import Installations from './pages/clients/Installations';
import InstallationsMap from './pages/clients/InstallationsMap';
import InstallationApproval from './pages/clients/InstallationApproval';
import Traffic from './pages/clients/Traffic';
import Statistics from './pages/clients/Statistics';
import ClientEquipmentMap from './pages/clients/ClientEquipmentMap';
import ClientConnectionMap from './pages/clients/ClientConnectionMap';
import ImportMikrotikClients from './pages/clients/ImportMikrotikClients';

// Componentes de finanzas
import FinanceDashboard from './pages/finance/Dashboard';
import InvoiceList from './pages/finance/InvoiceList';
import InvoiceDetail from './pages/finance/InvoiceDetail';
import PaymentReports from './pages/finance/PaymentReports';
import PaymentMethods from './pages/finance/PaymentMethods';
import Expenses from './pages/finance/Expenses';
import Accounting from './pages/finance/Accounting';
import BillingServicesCatalog from './pages/finance/BillingServicesCatalog';
import Promotions from './pages/finance/Promotions';

// Componentes de soporte
import SupportTickets from './pages/support/SupportTickets';
import KnowledgeBase from './pages/support/KnowledgeBase';

// Componentes de inventario
import InventoryDashboard from './pages/inventory/InventoryDashboard';
import InventoryItems from './pages/inventory/InventoryItems';
import InventoryTransactions from './pages/inventory/InventoryTransactions';
import InventoryPurchases from './pages/inventory/InventoryPurchases';
import NewPurchase from './pages/inventory/NewPurchase';
import EditPurchase from './pages/inventory/EditPurchase';
import InventoryCategories from './pages/inventory/InventoryCategories';
import InventoryWarehouses from './pages/inventory/InventoryWarehouses';
import LowStockItems from './pages/inventory/LowStockItems';
import InventoryReports from './pages/inventory/InventoryReports';
import TechnicianInventoryManagement from './pages/inventory/TechnicianInventoryManagement';
import IndividualEquipmentList from './pages/inventory/IndividualEquipmentList';
import InventoryKits from './pages/inventory/InventoryKits';

// Componentes de red
import WispGisCenter from './pages/network/WispGisCenter';
import NetworkCoverageMap from './pages/network/NetworkCoverageMap';
import LiveTechnicianMap from './pages/network/LiveTechnicianMap';

// Componentes de sistema
import RouterList from './pages/system/RouterList';
import RouterSyncStatus from './pages/system/RouterSyncStatus';
import InternetPlans from './pages/system/InternetPlans';
import Zones from './pages/system/Zones';
import AccessPoints from './pages/system/AccessPoints';
import Nodes from './pages/system/Nodes';
import PtpLinks from './pages/system/PtpLinks';
import Technicians from './pages/system/Technicians';
import Roles from './pages/system/Roles';
import Users from './pages/system/Users';
import CompanySettings from './pages/system/CompanySettings';
import PaymentGatewayConfig from './pages/system/PaymentGatewayConfig';
import WarningDesigner from './pages/system/WarningDesigner';
import InvoiceTemplateEditor from './pages/system/InvoiceTemplateEditor';
import WhatsAppSettings from './pages/system/WhatsAppSettings';
import UserProfile from './pages/system/UserProfile';
import LandingPage from './pages/LandingPage';
import LandingManager from './pages/landingAdmin/LandingManager';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import TenantSignup from './pages/auth/TenantSignup';

// Helper: detecta si el host actual corresponde a la Landing Page principal o a un panel de administración WISP
function isLandingPageDomain() {
  const hostname = window.location.hostname;
  return hostname === 'yinowisp.com' || hostname === 'www.yinowisp.com' || hostname === 'yinowisp.net' || hostname === 'www.yinowisp.net';
}

function isAdminSubdomain() {
  const hostname = window.location.hostname;
  if (isLandingPageDomain()) {
    return false;
  }
  if (hostname.startsWith('cliente.') || hostname.startsWith('tecnico.') || hostname.startsWith('aviso.')) {
    return false;
  }
  return true;
}

// Guard: solo permite acceso a rutas admin desde subdominios de administración WISP
function AdminOnly({ children }) {
  if (!isAdminSubdomain()) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Componente que decide qué mostrar en la ruta raíz "/"
function RootRoute() {
  if (isAdminSubdomain()) {
    // En subdominios WISP (ej: alfa.yinowisp.com, admin.yinowisp.com) → redirigir al login del panel
    return <Navigate to="/login" replace />;
  }
  // En el dominio principal (yinowisp.com) → mostrar la Landing Page
  return <LandingPage />;
}

function App() {
  console.log("App - Renderizando");

  // CORREGIDO: Simplificar la lógica de separación de sesiones
  React.useEffect(() => {
    const currentPath = window.location.pathname;

    // Solo limpiar sesiones cuando realmente sea necesario
    // No limpiar automáticamente, dejar que cada portal maneje su propia autenticación

    console.log('Ruta actual:', currentPath);

    // Portal de cliente - limpiar solo si viene de admin
    if (currentPath.startsWith("/client/") && localStorage.getItem('token')) {
      console.log('Accediendo al portal de cliente, limpiando sesión de administrador');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }

    // Portal del técnico - limpiar solo si viene de admin
    if (currentPath.startsWith("/technician/") && localStorage.getItem('token')) {
      console.log('Accediendo al portal de técnico, limpiando sesión de administrador');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }

    // Panel de administración - limpiar solo si viene de otros portales
    if (!currentPath.startsWith("/client/") && !currentPath.startsWith("/technician/")) {
      if (localStorage.getItem('clientToken')) {
        console.log('Accediendo al panel de administración, limpiando sesión de cliente');
        localStorage.removeItem('clientToken');
        localStorage.removeItem('clientData');
      }
      if (localStorage.getItem('technicianToken')) {
        console.log('Accediendo al panel de administración, limpiando sesión de técnico');
        localStorage.removeItem('technicianToken');
        localStorage.removeItem('technicianData');
      }
    }
  }, []);

  return (
    <BrowserRouter>
    <AuthProvider>
      <Routes>
        {/* Portal del Cliente */}
        <Route path="/client/*" element={<ClientPortalApp />} />

        {/* Portal del Técnico */}
        <Route path="/technician/*" element={<TechnicianPortalApp />} />

        {/* Ruta de Login - SOLO accesible desde admin.yinowisp.com */}
        <Route path="/login" element={
          <AdminOnly>
            <LoginForm />
          </AdminOnly>
        } />

        {/* RUTAS DE PANEL DE ADMINISTRACIÓN */}

        {/* Ruta raíz: Landing Page en yinowisp.com, Redirect a login en admin.yinowisp.com */}
        <Route
          path="/"
          element={<RootRoute />}
        />

        <Route
          path="/signup"
          element={<TenantSignup />}
        />

        {/* Dashboard - No requiere permisos específicos, solo autenticación */}
        <Route
          path="/admin"
          element={
          <AdminOnly>
            <PrivateRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        {/* Clientes - Requiere permisos específicos */}
        <Route
          path="/clients/list"
          element={
          <AdminOnly>
            <PrivateRoute permission="clients.view">
                <Layout>
                  <ClientList />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/clients/add"
          element={
          <AdminOnly>
            <PrivateRoute permission="clients.create">
                <Layout>
                  <AddEditClient />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/clients/import-mikrotik"
          element={
          <AdminOnly>
            <PrivateRoute permission="clients.create">
                <Layout>
                  <ImportMikrotikClients />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/clients/edit/:id"
          element={
          <AdminOnly>
            <PrivateRoute permission="clients.edit">
                <Layout>
                  <AddEditClient />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/clients/installations"
          element={
          <AdminOnly>
            <PrivateRoute permission="clients.view">
                <Layout>
                  <Installations />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/clients/installations/map"
          element={
          <AdminOnly>
            <PrivateRoute permission="clients.view">
                <Layout>
                  <WispGisCenter />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/clients/installations/pending"
          element={
          <AdminOnly>
            <PrivateRoute permission="clients.view">
                <Layout>
                  <InstallationApproval />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/clients/traffic"
          element={
          <AdminOnly>
            <PrivateRoute permission="clients.traffic.view">
                <Layout>
                  <Traffic />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/clients/statistics"
          element={
          <AdminOnly>
            <PrivateRoute permission="clients.view">
                <Layout>
                  <Statistics />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/clients/equipment-map"
          element={
          <AdminOnly>
            <PrivateRoute permission="mapa.de.equipos.de.clientes">
                <Layout>
                  <ClientEquipmentMap />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/clients/connection-map"
          element={
          <AdminOnly>
            <PrivateRoute permission="clients.view">
                <Layout>
                  <WispGisCenter />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        {/* Finanzas - Requiere permisos específicos */}
        <Route
          path="/finance/dashboard"
          element={
          <AdminOnly>
            <PrivateRoute permission="finance.reports.view">
                <Layout>
                  <FinanceDashboard />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/finance/invoices"
          element={
          <AdminOnly>
            <PrivateRoute permission="invoices.view">
                <Layout>
                  <InvoiceList />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/finance/invoices/:id"
          element={
          <AdminOnly>
            <PrivateRoute permission="invoices.view">
                <Layout>
                  <InvoiceDetail />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/finance/pending-payments"
          element={<Navigate to="/finance/invoices?tab=pending" replace />}
        />

        <Route
          path="/finance/payment-reports"
          element={
          <AdminOnly>
            <PrivateRoute permission="finance.reports.view">
                <Layout>
                  <PaymentReports />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/finance/payment-methods"
          element={
          <AdminOnly>
            <PrivateRoute permission="payments.create">
                <Layout>
                  <PaymentMethods />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/finance/billing-services"
          element={
          <AdminOnly>
            <PrivateRoute permission="invoices.view">
                <Layout>
                  <BillingServicesCatalog />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/finance/promotions"
          element={
          <AdminOnly>
            <PrivateRoute permission="invoices.view">
                <Layout>
                  <Promotions />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/finance/expenses"
          element={
          <AdminOnly>
            <PrivateRoute permission="finance.reports.view">
                <Layout>
                  <Expenses />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/finance/accounting"
          element={
          <AdminOnly>
            <PrivateRoute permission="finance.reports.view">
                <Layout>
                  <Accounting />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        {/* Soporte */}
        <Route
          path="/support/tickets"
          element={
          <AdminOnly>
            <PrivateRoute permission="support.tickets">
                <Layout>
                  <SupportTickets />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/support/knowledge"
          element={
          <AdminOnly>
            <PrivateRoute permission="support.knowledge">
                <Layout>
                  <KnowledgeBase />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        {/* Inventario - Requiere permisos específicos */}
        <Route
          path="/inventory/dashboard"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.view">
                <Layout>
                  <InventoryDashboard />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/inventory/items"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.view">
                <Layout>
                  <InventoryItems />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/inventory/items/new"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.view">
                <Layout>
                  <InventoryItems openNewOnLoad={true} />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/inventory/transactions"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.transactions.view">
                <Layout>
                  <InventoryTransactions />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/inventory/purchases"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.purchases.view">
                <Layout>
                  <InventoryPurchases />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/inventory/purchases/new"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.purchases.manage">
                <Layout>
                  <NewPurchase />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/inventory/purchases/edit/:id"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.purchases.manage">
                <Layout>
                  <EditPurchase />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/inventory/categories"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.manage">
                <Layout>
                  <InventoryCategories />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/inventory/warehouses"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.manage">
                <Layout>
                  <InventoryWarehouses />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        {/* NUEVA RUTA: Gestión de inventario de técnicos */}
        <Route
          path="/inventory/technicians"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.manage">
                <Layout>
                  <TechnicianInventoryManagement />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        {/* NUEVA RUTA: Gestión de equipos individuales (series) */}
        <Route
          path="/inventory/equipment"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.view">
                <Layout>
                  <IndividualEquipmentList />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        {/* NUEVA RUTA: Packs / Kits de Instalación */}
        <Route
          path="/inventory/kits"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.view">
                <Layout>
                  <InventoryKits />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/inventory/low-stock"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.view">
                <Layout>
                  <LowStockItems />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/inventory/reports"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.reports.view">
                <Layout>
                  <InventoryReports />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/inventory/client-equipment-map"
          element={
          <AdminOnly>
            <PrivateRoute permission="inventory.view">
                <Layout>
                  <ClientEquipmentMap />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        {/* Red - Requiere permisos específicos */}
        <Route
          path="/network/coverage-map"
          element={
          <AdminOnly>
            <PrivateRoute permission="clients.view">
                <Layout>
                  <WispGisCenter />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/network/technicians-map"
          element={
          <AdminOnly>
            <PrivateRoute permission="clients.view">
                <Layout>
                  <LiveTechnicianMap />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        {/* Sistema - Requiere permisos específicos */}
        <Route
          path="/system/routers"
          element={
          <AdminOnly>
            <PrivateRoute permission="routers.manage">
                <Layout>
                  <RouterList />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/sync-queue"
          element={
          <AdminOnly>
            <PrivateRoute permission="routers.manage">
                <Layout>
                  <RouterSyncStatus />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/internet-plans"
          element={
          <AdminOnly>
            <PrivateRoute permission="plans.manage">
                <Layout>
                  <InternetPlans />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/zones"
          element={
          <AdminOnly>
            <PrivateRoute permission="zones.manage">
                <Layout>
                  <Zones />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/nodes"
          element={
          <AdminOnly>
            <PrivateRoute permission="zones.manage">
                <Layout>
                  <Nodes />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/access-points"
          element={
          <AdminOnly>
            <PrivateRoute permission="routers.manage">
                <Layout>
                  <AccessPoints />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/ptp-links"
          element={
          <AdminOnly>
            <PrivateRoute permission="routers.manage">
                <Layout>
                  <PtpLinks />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/technicians"
          element={
          <AdminOnly>
            <PrivateRoute permission="clients.view">
                <Layout>
                  <Technicians />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/roles"
          element={
          <AdminOnly>
            <PrivateRoute permission="roles.manage">
                <Layout>
                  <Roles />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/users"
          element={
          <AdminOnly>
            <PrivateRoute permission="users.view">
                <Layout>
                  <Users />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/superadmin"
          element={
          <AdminOnly>
            <PrivateRoute>
                <Layout>
                  <SuperAdminDashboard />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/settings"
          element={
          <AdminOnly>
            <PrivateRoute permission="roles.manage">
                <Layout>
                  <CompanySettings />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/payment-gateways"
          element={
          <AdminOnly>
            <PrivateRoute permission="roles.manage">
                <Layout>
                  <PaymentGatewayConfig />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/warning-designer"
          element={
          <AdminOnly>
            <PrivateRoute permission="roles.manage">
                <Layout>
                  <WarningDesigner />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/template-editor"
          element={
          <AdminOnly>
            <PrivateRoute permission="roles.manage">
                <Layout>
                  <InvoiceTemplateEditor />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/whatsapp"
          element={
          <AdminOnly>
            <PrivateRoute permission="roles.manage">
                <Layout>
                  <WhatsAppSettings />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/landing"
          element={
          <AdminOnly>
            <PrivateRoute permission="roles.manage">
                <Layout>
                  <LandingManager />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        <Route
          path="/system/profile"
          element={
          <AdminOnly>
            <PrivateRoute>
                <Layout>
                  <UserProfile />
                </Layout>
              </PrivateRoute>
          </AdminOnly>
          }
        />

        {/* Ruta por defecto - redirigir según subdominio */}
        <Route path="*" element={isAdminSubdomain() ? <Navigate to="/login" replace /> : <Navigate to="/" replace />} />
      </Routes>
        </AuthProvider>
  </BrowserRouter>
  );
}

export default App;